import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  doc,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

const FOLDERS_COLLECTION = 'folders';
const DEFAULT_FOLDER_NAME = 'All Videos';

/**
 * Creates a new folder for a user
 * @param {string} userId - User ID
 * @param {string} folderName - Folder name
 * @returns {Promise<string>} - Folder document ID
 */
export const createFolder = async (userId, folderName) => {
  if (!userId || !folderName?.trim()) {
    throw new Error('User ID and folder name are required');
  }

  // Prevent creating a folder with the default name
  if (folderName.trim() === DEFAULT_FOLDER_NAME) {
    throw new Error(`"${DEFAULT_FOLDER_NAME}" is a reserved folder name`);
  }

  try {
    // Check if folder with same name already exists for this user
    const existingQuery = query(
      collection(db, FOLDERS_COLLECTION),
      where('userId', '==', userId),
      where('name', '==', folderName.trim())
    );

    const existing = await getDocs(existingQuery);
    if (!existing.empty) {
      throw new Error('A folder with this name already exists');
    }

    const docRef = await addDoc(collection(db, FOLDERS_COLLECTION), {
      userId,
      name: folderName.trim(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return docRef.id;
  } catch (error) {
    console.error('Error creating folder:', error);
    throw error;
  }
};

/**
 * Gets all folders for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Array of folder objects
 */
export const getUserFolders = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const foldersQuery = query(
      collection(db, FOLDERS_COLLECTION),
      where('userId', '==', userId),
      orderBy('createdAt', 'asc')
    );

    const querySnapshot = await getDocs(foldersQuery);
    const folders = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      folders.push({
        id: doc.id,
        userId: data.userId,
        name: data.name,
        createdAt: data.createdAt?.toMillis ? data.createdAt.toMillis() : 
                  (data.createdAt?.toDate ? data.createdAt.toDate().getTime() : null),
        updatedAt: data.updatedAt?.toMillis ? data.updatedAt.toMillis() : 
                  (data.updatedAt?.toDate ? data.updatedAt.toDate().getTime() : null),
      });
    });

    return folders;
  } catch (error) {
    console.error('Error fetching folders:', error);
    // If index error, try without orderBy
    if (error.code === 'failed-precondition') {
      try {
        const foldersQuery = query(
          collection(db, FOLDERS_COLLECTION),
          where('userId', '==', userId)
        );
        const querySnapshot = await getDocs(foldersQuery);
        const folders = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          folders.push({
            id: doc.id,
            userId: data.userId,
            name: data.name,
            createdAt: data.createdAt?.toMillis ? data.createdAt.toMillis() : 
                      (data.createdAt?.toDate ? data.createdAt.toDate().getTime() : null),
            updatedAt: data.updatedAt?.toMillis ? data.updatedAt.toMillis() : 
                      (data.updatedAt?.toDate ? data.updatedAt.toDate().getTime() : null),
          });
        });
        // Sort in memory
        folders.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
        return folders;
      } catch (fallbackError) {
        console.error('Fallback query also failed:', fallbackError);
        return [];
      }
    }
    return [];
  }
};

/**
 * Renames a folder
 * @param {string} folderId - Folder document ID
 * @param {string} newName - New folder name
 * @returns {Promise<void>}
 */
export const renameFolder = async (folderId, newName) => {
  if (!folderId || !newName?.trim()) {
    throw new Error('Folder ID and new name are required');
  }

  if (newName.trim() === DEFAULT_FOLDER_NAME) {
    throw new Error(`"${DEFAULT_FOLDER_NAME}" is a reserved folder name`);
  }

  try {
    // Get the folder to check userId
    const folderRef = doc(db, FOLDERS_COLLECTION, folderId);
    const folderDoc = await getDoc(folderRef);
    
    if (!folderDoc.exists()) {
      throw new Error('Folder not found');
    }

    const folderData = folderDoc.data();
    
    // Check if another folder with this name exists for the same user
    const existingQuery = query(
      collection(db, FOLDERS_COLLECTION),
      where('userId', '==', folderData.userId),
      where('name', '==', newName.trim())
    );
    const existing = await getDocs(existingQuery);
    if (!existing.empty && existing.docs[0].id !== folderId) {
      throw new Error('A folder with this name already exists');
    }

    await updateDoc(folderRef, {
      name: newName.trim(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error renaming folder:', error);
    throw error;
  }
};

/**
 * Deletes a folder (does NOT delete the URLs, they just become unfolder'd)
 * @param {string} folderId - Folder document ID
 * @param {string} userId - User ID (for security verification)
 * @returns {Promise<void>}
 */
export const deleteFolder = async (folderId, userId = null) => {
  if (!folderId) {
    throw new Error('Folder ID is required');
  }

  if (!userId) {
    throw new Error('User ID is required to delete a folder');
  }

  try {
    // First, verify the folder exists and belongs to the user
    const folderRef = doc(db, FOLDERS_COLLECTION, folderId);
    const folderDoc = await getDoc(folderRef);
    
    if (!folderDoc.exists()) {
      throw new Error('Folder not found');
    }

    const folderData = folderDoc.data();
    
    // Verify ownership - this is critical for security
    if (folderData.userId !== userId) {
      throw new Error('You do not have permission to delete this folder');
    }

    // Remove folder reference from all history items in this folder
    // Only update items that belong to the same user
    const historyCollectionRef = collection(db, 'youtubeHistory');
    const historyItemsQuery = query(
      historyCollectionRef,
      where('folderId', '==', folderId),
      where('userId', '==', userId) // Add userId filter to ensure we only get user's items
    );
    
    const historyItems = await getDocs(historyItemsQuery);
    const updatePromises = historyItems.docs.map(docItem => {
      const docRef = doc(db, 'youtubeHistory', docItem.id);
      return updateDoc(docRef, {
        folderId: null,
        updatedAt: serverTimestamp(),
      });
    });

    await Promise.all(updatePromises);

    // Then delete the folder
    await deleteDoc(folderRef);
  } catch (error) {
    console.error('Error deleting folder:', error);
    throw error;
  }
};

/**
 * Gets the default "All Videos" folder (virtual folder)
 * @returns {Object} - Default folder object
 */
export const getDefaultFolder = () => {
  return {
    id: 'all-videos',
    name: DEFAULT_FOLDER_NAME,
    isDefault: true,
  };
};

